import { formatLocale } from 'd3-format';

import Axis from '../Axis';
import canvasEnums from '../enums/canvas';
import subpixel from '../util/canvasSubpixelOffset';

/** A wrapper for the collection of d3 scales and related functions */
export default class AxisNumber extends Axis {
	/**
	 * Return y label density figure
	 * @param {object} size - panel size parameters
	 * @property {number} size.height - height of the panel
	 * @returns {number}
	 * @private
	 */
	getNumTicksY(size) {
		const { spacing, threshold } = this.panel.params.style.yAxis.label;
		return size.height < threshold.small
			? Math.ceil(size.height / spacing.small)
			: Math.ceil(size.height / spacing.default);
	}

	/**
	 * Return id of the appropriate label bucket (micro, small, default, large)
	 * @param {number} step - the step between two consecutive ticks
	 * @param {number} precisionMultiple - (example: 5) allows certain internal calls (Fibonacci Tool, Flags) to override the micro and small buckets logic
	 * @returns {string} label bucket id
	 * @private
	 */
	getFormatType(step = 100, precisionMultiple = 1, panel) {
		const { format: stepFormat } = panel.params.style.yAxis.label;
		if (step >= stepFormat.large.threshold) {
			return 'large';
		} else if (step < stepFormat.micro.threshold * precisionMultiple) {
			return 'micro';
		} else if (step < stepFormat.small.threshold * precisionMultiple) {
			return 'small';
		}

		return 'default';
	}

	/**
	 * Return a d3 format string for use in a format statement
	 * @param {d3-scale} yScale - Yaxis scale
	 * @param {number} [precisionMultiple] - optional precision multiplier. Defaults to 1
	 * @returns {string}
	 */
	getFormatString(yScale, precisionMultiple = 1, chart = this.core) {
		const { panel } = this;
		const { size } = panel;
		const domain = yScale.domain();
		const yTicks = yScale.ticks(this.getNumTicksY(size, chart)).reverse();
		let step;
		let formatString;

		// get the step between two consecutive ticks
		if (yTicks.length > 1) {
			step = yTicks[1] - yTicks[0];
		} else if (yTicks.length && yTicks[0] !== 0) {
			step = yTicks[0];
		} else {
			step = Math.abs(domain[1] - domain[0]);
		}
		const formatType = this.getFormatType(step, precisionMultiple, panel);
		const yLabelFormat = panel.params.style.yAxis.label.format[formatType];
		if (panel.params.yAxis.format === 'percent' || panel.isNormalized) {
			// percent-change formatting
			formatString = yLabelFormat.formatPercent;
		} else {
			// default formatting
			formatString = yLabelFormat.format;
		}

		// remove decimal places on large steps and values
		if (step > 2 && yTicks.length && yTicks[yTicks.length - 1] >= 1000 && formatType !== 'large') {
			formatString = formatString.replace(/\.\d/, '.0');
		}

		return formatString;
	}

	/**
	 * Formats the value per the format string passed in
	 * @param {number} val - Number to format
	 * @param {string} formatString - The d3-format specifier that will be used to format the number
	 * @param {boolean} isNormalized - Whether or not chart data is normalized
	 * @returns {string}
	 */
	getFormatValue(val, formatString = '', isNormalized = this.panel.isNormalized) {
		const { panel } = this;

		// Divide by 100 because the % modifier will multiply by 100 in d3-format
		if (
			panel.params.yAxis.format === 'percent' ||
			(/%$/.test(formatString) && isNormalized) // Don't change val if no % modifier is present
		) {
			val /= 100;
		}

		const valOut = formatLocale(this.core.locale).format(formatString)(val);

		// Use B for billion instead of G for giga
		return valOut.replace(/G/, 'B');
	}

	/**
	 * Calculate the labels that will be rendered and their positions.
	 * @param {d3-scale} yScale - Yaxis scale
	 * @param {object} chart - The chart object
	 * @returns {Array} - Array of objects with labels and their positions
	 */
	calculateRenderedLabels(yScale = this.primaryScale, chart = this.core) {
		const { panel } = this;
		const { size } = panel;
		const { style } = panel.params;
		const numTicksY = this.getNumTicksY(size, chart);
		const yScaleTicks = yScale.ticks(numTicksY).reverse();
		const yFormatString = this.getFormatString(yScale, 1, chart);
		const labels = [];
		const halfFontHeight = Math.ceil(style.yAxis.font.size / 2) + 2;
		let lastY = null;

		yScaleTicks.forEach((yTick, i) => {
			const y0 = subpixel.px(yScale(yTick));
			let yWithPadding;

			// Adjust y0 for text baseline setting
			switch (style.yAxis.label.textBaseline) {
				case canvasEnums.textBaseline.ALPHABETIC:
					yWithPadding = y0 - style.yAxis.label.padding.bottom;
					break;
				case canvasEnums.textBaseline.HANGING:
					yWithPadding = y0 + style.yAxis.label.padding.top;
					break;
				case canvasEnums.textBaseline.MIDDLE:
				default:
					yWithPadding = y0;
			}

			const bottomCollide = i === 0 && y0 > size.height - halfFontHeight;
			const topCollide = i === yScaleTicks.length - 1 && y0 < halfFontHeight;

			if (!bottomCollide && !topCollide) {
				if (!lastY || y0 + halfFontHeight < lastY) {
					const label = this.getFormatValue(yTick, yFormatString);
					labels.push({
						label,
						y0: yWithPadding
					});
					lastY = yWithPadding;
				}
			}
		});
		return labels;
	}

	/**
	 * Calculate label left position based on yAxisSide and padding
	 * @param {string} yAxisSide - The side of the Y-axis ('left' or 'right')
	 * @param {object} size - The size of the panel
	 * @param {object} style - The style parameters
	 * @returns {number} - The calculated x0 value
	 */
	getLabelXPosition(yAxisSide, size, style) {
		if (yAxisSide === 'left') {
			return size.padding.left - style.yAxis.label.padding.left;
		} else {
			return size.padding.left + size.width + style.yAxis.label.padding.left;
		}
	}

	/**
	 * Render the Y-axis
	 * @param {Array} labels - Array of precomputed Y-axis labels with positions
	 * @param {d3-scale} yScale - Yaxis scale
	 * @param {object} chart - The chart object
	 */
	render(chart = this.core, labels = this.calculateRenderedLabels(this.primaryScale)) {
		const { panel } = this;
		const { size } = panel;
		const { style } = panel.params;
		const ctx = panel.rootContext;
		this._renderedLabels = [];
		const x0 = this.getLabelXPosition(chart.params.yAxisSide, size, style);
		const yTicks = [];

		// Prepare context for labels
		ctx.beginPath();
		ctx.textAlign = style.yAxis.label.textAlign;
		ctx.textBaseline = style.yAxis.label.textBaseline;
		ctx.fillStyle = style.yAxis.label.color;
		ctx.font = panel.getFontStyle(
			style.yAxis.font.weight,
			style.yAxis.font.size,
			style.yAxis.font.family,
			style.yAxis.font.style
		);

		// Render Y labels
		labels.forEach(({ label, y0 }) => {
			ctx.fillText(label, x0, y0);
			yTicks.push(y0);
			this._renderedLabels.push(label); // Store for testing
		});

		// Draw horizontal grid lines
		panel.grid.renderHorizontalGrid(yTicks);

		// Draw vertical YAxis label
		if (chart.params.showVerticalPanelLegend) {
			this._renderVerticalLegend(ctx, style);
		}
	}

	/**
	 * Draws a vertical YAxis label if it fits
	 * @param {CanvasRenderingContext2D} ctx - The canvas context
	 */
	_renderVerticalLegend(ctx) {
		const { panel, core } = this;
		const { style } = panel.params;
		const { callbackManager } = core;
		const isLegendCallbackRegistered = callbackManager.isRegistered('PANEL_LEGEND_VERTICAL_LABEL');
		const legendText = isLegendCallbackRegistered
			? callbackManager.emit('PANEL_LEGEND_VERTICAL_LABEL', {
				indicatorParams: panel.indicators[0].params,
				primarySymbol: core.xref[core.params.symbol]
			})
			: panel.indicators[0].params.name;

		// Determine offsetX (distance from right edge of panel)
		const defaultFontSize = style.yAxis.label.vertical.right.font.size;
		const offsetX = style.yAxis.label.vertical.right.offsetX || defaultFontSize;
		const availableVerticalSpace = panel.size.height;

		// Set up canvas context for label
		this._setupVerticalLabelContext(ctx, style.yAxis.label.vertical.right);

		// Check if the label fits
		if (this._doesVerticalLabelFit(ctx, legendText, availableVerticalSpace)) {
			const legendX = panel.size.padding.left + panel.size.width + panel.size.padding.right - offsetX;
			const legendY = panel.size.height / 2; // Center vertically

			// Draw the label
			this._drawVerticalLabel(ctx, legendText, legendX, legendY);
		}
	}

	/**
	 * Set up the canvas context for drawing labels
	 * @param {CanvasRenderingContext2D} ctx - The canvas context
	 * @param {Object} style - Style settings for the label
	 */
	_setupVerticalLabelContext(ctx, style) {
		ctx.save(); // Save the current state
		ctx.fillStyle = style.color;
		ctx.font = this.panel.getFontStyle(style.font.weight, style.font.size, style.font.family, style.font.style);
		ctx.textAlign = 'left';
		ctx.textBaseline = 'middle';
	}

	/**
	 * Draws the label on the canvas
	 * @param {CanvasRenderingContext2D} ctx - The canvas context
	 * @param {string} text - The text of the label
	 * @param {number} x - X position for the label
	 * @param {number} y - Y position for the label
	 */
	_drawVerticalLabel(ctx, text, x, y) {
		const textWidth = ctx.measureText(text).width;
		ctx.translate(x, y);
		ctx.rotate(Math.PI / 2); // Rotate 90 degrees
		ctx.fillText(text, -textWidth / 2, 0); // Adjust position
		ctx.restore();
	}

	/**
	 * Checks if the label fits within the available space
	 * @param {CanvasRenderingContext2D} ctx - The canvas context
	 * @param {string} text - The text of the label
	 * @param {number} availableSpace - The available space to fit the label
	 * @returns {boolean} - True if the label fits, false otherwise
	 */
	_doesVerticalLabelFit(ctx, text, availableSpace) {
		const textWidth = ctx.measureText(text).width;
		return textWidth <= availableSpace;
	}
}
